import { Show, For, createMemo, createSignal } from "solid-js";
import { useNavigate, useParams } from "@solidjs/router";
import { FaSolidChevronDown, FaSolidPlus, FaSolidGear, FaSolidBuilding } from "solid-icons/fa";
import { TbOutlineLayoutDashboard } from "solid-icons/tb";
import { useSession, useListOrganizations, organization as orgClient } from "../lib/auth-client";
import { Text } from "./i18n";
import { setIsOrganizationSwitching, userIsAdmin } from "./signals";
import { useProjectPermissions } from "./hooks/useProjectPermissions";
import { CreateOrganizationDialog } from "./CreateOrganizationDialog";

export const OrganizationSwitcher = () => {
  const session = useSession();
  const { canManageOrg } = useProjectPermissions();
  const organizations = useListOrganizations();
  const navigate = useNavigate();
  const params = useParams();

  const [createDialogOpen, setCreateDialogOpen] = createSignal(false);

  const activeOrgId = createMemo(() => session()?.data?.session.activeOrganizationId);
  const organizationsData = createMemo(() => organizations()?.data);

  // Find the active organization from the organizations list
  const activeOrg = createMemo(() => {
    const orgId = activeOrgId();
    if (!orgId) return null;
    return organizationsData()?.find((o: any) => o.id === orgId);
  });

  const handleSwitchOrg = async (orgId: string | null) => {
    try {
      setIsOrganizationSwitching(true);
      if (orgId) {
        await orgClient.setActive({ organizationId: orgId });
        const org = organizationsData()?.find((o: any) => o.id === orgId);
        if (org) {
          navigate(`/${org.slug}`);
        }
      } else {
        navigate("/");
      }
    } finally {
      setTimeout(() => {
        // setTimeout is needed so session().isRefetching becomes true as a side effect of orgClient.setActive
        // and before we set setIsOrganizationSwitching to false (for the effect in TenantLayout)
        setIsOrganizationSwitching(false);
      });
    }
  };

  return (
    <div class="ui-dropdown ui-dropdown-end">
      <div tabindex="0" role="button" class="ui-btn ui-btn-ghost gap-1">
        <Show when={activeOrg()} fallback={<Text message="No organization" />}>
          <Show when={activeOrg()!.slug === params.tenantId}>{activeOrg()!.name}</Show>
          <Show when={activeOrg()!.slug !== params.tenantId}>
            <Text message="Select organization" />
          </Show>
        </Show>
        <FaSolidChevronDown size={12} />
      </div>
      <ul
        tabindex="-1"
        class="ui-dropdown-content ui-menu bg-base-100 rounded-box border-base-200 z-10 w-56 border p-2 shadow-md"
      >
        <Show when={userIsAdmin()}>
          <li>
            <button
              type="button"
              class="flex items-center gap-2"
              onClick={() => {
                // @ts-ignore
                document.activeElement.blur();
                navigate("/admin/organizations");
              }}
            >
              <TbOutlineLayoutDashboard size={16} class="text-base-content/60" />
              <Text message="Admin Dashboard" />
            </button>
          </li>
          <li class="ui-menu-title py-1">
            <hr class="border-base-200" />
          </li>
        </Show>
        <Show when={activeOrg() && canManageOrg()}>
          <li>
            <button
              type="button"
              class="flex items-center gap-2"
              onClick={() => {
                // @ts-ignore
                document.activeElement.blur();
                navigate(`/${activeOrg()!.slug}/settings`);
              }}
            >
              <FaSolidGear size={14} class="text-base-content/60" />
              <Text message="Manage {name}" name={activeOrg()!.name} />
            </button>
          </li>
          <li class="ui-menu-title py-1">
            <hr class="border-base-200" />
          </li>
        </Show>
        <li>
          <button
            class="flex items-center gap-2"
            onClick={() => {
              // @ts-ignore
              document.activeElement.blur();
              setCreateDialogOpen(true);
            }}
          >
            <FaSolidPlus size={14} class="text-base-content/60" />
            <Text message="Create Organization" />
          </button>
        </li>
        <Show when={organizationsData()?.length}>
          <li class="ui-menu-title py-1">
            <hr class="border-base-200" />
          </li>
          <li class="ui-menu-title text-base-content/50 px-2 py-1 text-xs font-medium tracking-wide uppercase">
            <Text message="Organizations" />
          </li>
        </Show>
        <For each={organizationsData()}>
          {(org: any) => (
            <li>
              <button
                class="flex items-center gap-2"
                onClick={() => {
                  // If we're at / and not /tenantId, navigate
                  if (params.tenantId === undefined && org.slug !== params.tenantId) {
                    navigate(`/${org.slug}`);
                  } else {
                    if (activeOrg()?.id !== org.id) {
                      handleSwitchOrg(org.id);
                    }
                    // @ts-ignore
                    document.activeElement.blur();
                  }
                }}
                classList={{ "ui-menu-active": activeOrg()?.id === org.id && org.slug === params.tenantId }}
              >
                <FaSolidBuilding size={14} class="text-base-content/60" />
                {org.name || org.slug}
              </button>
            </li>
          )}
        </For>
      </ul>
      <CreateOrganizationDialog open={createDialogOpen()} onClose={() => setCreateDialogOpen(false)} />
    </div>
  );
};
